import React, { useState, useEffect } from "react";
import useDigitalTheme from "../hooks/useDigitalTheme";
import { useSelector } from "react-redux";
import {
  FaClipboardCheck,
  FaKey,
  FaChartBar,
  FaQuestionCircle,
  FaHandshake,
} from "react-icons/fa";
import PageHero from "../../components/PageHero";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import BarraMenu from "../../components/BarraMenu";
import { getBusinessTheme } from "../../theme/businessThemes";
import digitalFooterLogo from "../../assets/logos/logodigital1T.png";
import "./styles.css";

// Constants
const AUTHORIZED_PROFILES = ["Administrador", "Evaluador", "Evaluador Externo"];
const ADMIN_PROFILES = ["Administrador"];
const EVALUATOR_PROFILES = ["Evaluador"];

const NAVIGATION_ITEMS = [
  {
    icon: FaHandshake,
    text: "Usuarios",
    path: "/gestionColaboradores",
    requiresAuth: true,
    allowedProfiles: ["Administrador"],
    description: "Creación y edición de usuarios",
  },
  {
    icon: FaHandshake,
    text: "Reportes",
    path: "/reporteEvaluaciones",
    requiresAuth: true,
    allowedProfiles: ["Administrador"],
    description: "Reporte de evaluaciones realizadas",
  },
  {
    icon: FaClipboardCheck,
    text: "Evaluaciones",
    path: "/realizarEvaluaciones",
    requiresAuth: true,
    allowedProfiles: ["Evaluador", "Evaluador Externo"],
    description: "Realizar evaluación / retroalimentación",
  },
  {
    icon: FaChartBar,
    text: "Reportes",
    path: "/reporteEvaluaciones",
    requiresAuth: true,
    allowedProfiles: ["Evaluador", "Evaluador Externo"],
    description: "Reportes de evaluaciones realizadas",
  },
  {
    icon: FaKey,
    text: "Cambiar Contraseñas",
    path: "/admonUsuariosExternos",
    requiresAuth: true,
    allowedProfiles: ["Administrador"],
    description: "Restaurar contraseñas",
  },
];

const Tooltip = ({ text, theme }: { text: string; theme: any; }) => (
  <div className="tooltip" style={{ backgroundColor: theme.tooltipBackground }}> {text}</div>
);

const NavigationCard = ({
  icon: Icon,
  text,
  path,
  isDisabled,
  description,
  theme,
}: {
  icon: any;
  text: string;
  path: string;
  isDisabled: boolean;
  description: string;
  theme: any;
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <motion.div
      whileHover={{ scale: isDisabled ? 1 : 1.05 }}
      whileTap={{ scale: isDisabled ? 1 : 0.95 }}
    >
      <Link
        className={`navigation-card ${isDisabled ? "disabled" : ""}`}
        to={isDisabled ? "#" : path}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <Icon className="navigation-icon" />
        <span className="navigation-text">{text}</span>
        {showTooltip && <Tooltip text={description} theme={theme} />}
        {isDisabled && <FaQuestionCircle className="restricted-icon" />}
      </Link>
    </motion.div>
  );
};
/* pie de pagina */
const Footer = () => (
  <motion.footer
    className="footer"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: 0.5 }}
  >
    <div className="text-center">
      <span style={{ color: "#003366", fontWeight: "normal" }}>
        © Digital ProfIT {new Date().getFullYear()}
      </span>
    </div>
  </motion.footer>
);
/* pie de pagina */
const PageLandingPage: React.FC = () => {
  useDigitalTheme();
  const { profile, first_name, idbusiness_group } = useSelector((state: any) => state.emp);
  const theme = getBusinessTheme(idbusiness_group);
  const isAuthorizedUser = AUTHORIZED_PROFILES.includes(profile);
  const [showWelcomeTitle, setShowWelcomeTitle] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowWelcomeTitle(false), 2200);
    return () => clearTimeout(timer);
  }, []);

  // Mensaje para usuarios sin acceso
  const UnauthorizedMessage = () => (
    <motion.div
      className="alert alert-warning text-center"
      style={{ maxWidth: "600px", margin: "2rem auto" }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <FaQuestionCircle size={50} className="mb-3" />
      <h4>Acceso Restringido</h4>
      <p className="mb-0">
        Su perfil actual <strong>({profile})</strong> no tiene permisos para acceder a esta aplicación.
        Por favor, contacte al administrador del sistema.
      </p>
    </motion.div>
  );

  return (
    <motion.div className={theme.isDarkLayout ? "home-outer" : "home-outer-plain"}>
    <motion.div className="container text-center home-container">
      <motion.div
        className="home-content"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <BarraMenu />
        <motion.div
          className={
            theme.isDarkLayout ? "home-panel home-panel--admin" : "home-panel home-panel--client"
          }
        >
        <PageHero
          centered
          titleContent={
            <div className="home-hero-title-wrap" aria-live="polite">
              <AnimatePresence mode="wait">
                {showWelcomeTitle ? (
                  <motion.h1
                    key="welcome"
                    className="home-hero-title"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ duration: 0.55, ease: [0.4, 0, 0.2, 1] }}
                  >
                    ¡Bienvenid@, {first_name}!
                  </motion.h1>
                ) : (
                  <motion.h1
                    key="system-title"
                    className="home-hero-title"
                    initial={{ opacity: 0, y: 22 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -14 }}
                    transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
                  >
                    Sistema de evaluación de desempeño
                  </motion.h1>
                )}
              </AnimatePresence>
            </div>
          }
        />

        {!isAuthorizedUser ? (
          <UnauthorizedMessage />
        ) : (
          <div className="navigation-grid">
            {NAVIGATION_ITEMS.filter(
              (item) => item.allowedProfiles.includes(profile)
            ).map((item, index) => (
              <NavigationCard
                key={index}
                icon={item.icon}
                text={item.text}
                path={item.path}
                isDisabled={false}
                description={item.description}
                theme={theme}
              />
            ))}
          </div>
        )}

        <div className="home-brand-footer">
          <motion.img
            src={digitalFooterLogo}
            alt="Digital ProfIT"
            className="floating-main_image"
          />
          <Footer />
        </div>
        </motion.div>
      </motion.div>
    </motion.div>
    </motion.div>
  );
};

export default PageLandingPage;